"use client";

import {
  AnimatePresence,
  motion,
  type MotionValue,
  useSpring,
  useTransform,
  useVelocity,
} from "motion/react";
import { useTranslations } from "next-intl";
import type { RefObject } from "react";

import { EASE_OUT_EXPO } from "@/components/motion/reveal";

import { TrainingDetails, type TrainingItem } from "./training-details";

// =============================================================================

type FloatingCardProps = {
  item: TrainingItem | null;
  x: MotionValue<number>;
  y: MotionValue<number>;
  cardRef: RefObject<HTMLDivElement | null>;
};

/**
 * Card following the pointer over the list (springs), slightly tilted by the
 * horizontal speed. Its content cross-fades from one row to the next.
 * Decorative: the same details are available to assistive technologies in
 * each row.
 */
export function FloatingCard({ item, x, y, cardRef }: FloatingCardProps) {
  const t = useTranslations("Trainings");
  const tCommon = useTranslations("Common");
  const velocityX = useVelocity(x);
  const tilt = useSpring(
    useTransform(velocityX, [-1600, 0, 1600], [-7, 0, 7], { clamp: true }),
    { stiffness: 180, damping: 22 }
  );

  return (
    <motion.div
      aria-hidden
      className="pointer-events-none fixed top-0 left-0 z-40"
      style={{ x, y, rotate: tilt }}
    >
      <AnimatePresence>
        {item && (
          <motion.div
            ref={cardRef}
            key="card"
            initial={{ opacity: 0, scale: 0.9, filter: "blur(6px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            exit={{
              opacity: 0,
              scale: 0.94,
              filter: "blur(4px)",
              transition: { duration: 0.25 },
            }}
            transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
            className="flex min-h-[clamp(20rem,31.4vw,28.25rem)] w-[clamp(20rem,31.4vw,28.25rem)] origin-top-left flex-col border border-line bg-surface-1/95 p-[clamp(1.25rem,1.75vw,1.6rem)] shadow-[0_2rem_4rem_-1.5rem_rgb(0_0_0/0.5)] backdrop-blur-md"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10, transition: { duration: 0.12 } }}
                transition={{ duration: 0.35, ease: EASE_OUT_EXPO }}
                className="flex flex-1 flex-col"
              >
                <TrainingDetails
                  item={item}
                  typeLabel={t(`types.${item.type}`)}
                  inProgressLabel={tCommon("inProgress")}
                  variant="card"
                />
              </motion.div>
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// =============================================================================
